import { useCallback } from 'react'
import useFormContext from '@/context/useFormContext'
import { ROUTES } from '@/config/routes'
import { FormState } from '@/context/formReducer'
import { useFormValidation } from '@/context/FormValidationContext'

interface ValidationRules {
  [key: string]: {
    statePath: string
    requiredFields: string[]
  }
}

const validationRules: ValidationRules = {
  // Personal routes
  [ROUTES.PERSONAL.ABOUT_YOU.path]: {
    statePath: 'personal.yourDetails',
    requiredFields: ['title', 'firstName', 'lastName', 'mobileNumber', 'email', 'dateOfBirth']
  },
  [ROUTES.PERSONAL.INCOME.path]: {
    statePath: 'personal.aboutYou',
    requiredFields: ['isJoint']
  },
  [ROUTES.PERSONAL.FINANCIAL_POSITION.path]: {
    statePath: 'personal.income',
    requiredFields: ['companyName', 'occupation', 'employmentType', 'incomeAmount']
  },
  [ROUTES.PERSONAL.DOC_UPLOAD.path]: {
    statePath: 'personal.financialPosition',
    requiredFields: ['isOwnHome']
  },
  // Business routes
  [ROUTES.BUSINESS.ABOUT_YOU.path]: {
    statePath: 'business.aboutYourBusiness',
    requiredFields: ['businessType', 'preferredLoan']
  },
  [ROUTES.BUSINESS.FINANCIAL_POSITION.path]: {
    statePath: 'business.aboutYou',
    requiredFields: ['firstName', 'lastName', 'phone', 'email']
  },
  [ROUTES.BUSINESS.APPLICATION_OPTIONS.path]: {
    statePath: 'business.financialPosition',
    requiredFields: ['isOwnHome']
  }
}

type StatePath = 
  | 'personal.yourDetails' 
  | 'personal.aboutYou' 
  | 'personal.income' 
  | 'personal.financialPosition'
  | 'business.aboutYourBusiness'
  | 'business.aboutYou'
  | 'business.financialPosition'

const getStateObject = (state: FormState, path: StatePath) => {
  switch (path) {
    case 'personal.yourDetails':
      return state.personal.yourDetails
    case 'personal.aboutYou':
      return state.personal.aboutYou
    case 'personal.income':
      return state.personal.income
    case 'personal.financialPosition':
      return state.personal.financialPosition
    case 'business.aboutYourBusiness':
      return state.business.aboutYourBusiness
    case 'business.aboutYou':
      return state.business.aboutYou
    case 'business.financialPosition':
      return state.business.financialPosition
    default:
      return null
  }
}

// Define the order of steps for both personal and business flows
const PERSONAL_FLOW = [
  ROUTES.PERSONAL.YOUR_DETAILS.path,
  ROUTES.PERSONAL.ABOUT_YOU.path,
  ROUTES.PERSONAL.INCOME.path,
  ROUTES.PERSONAL.FINANCIAL_POSITION.path,
  ROUTES.PERSONAL.DOC_UPLOAD.path
]

const BUSINESS_FLOW = [
  ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path,
  ROUTES.BUSINESS.ABOUT_YOU.path,
  ROUTES.BUSINESS.FINANCIAL_POSITION.path,
  ROUTES.BUSINESS.APPLICATION_OPTIONS.path
]

const isMovingForward = (currentPath: string, targetPath: string): boolean => {
  // If either path is not in our defined flows, return true to be safe
  if (!PERSONAL_FLOW.includes(currentPath) && !BUSINESS_FLOW.includes(currentPath)) {
    return true
  }

  const flow = PERSONAL_FLOW.includes(currentPath) ? PERSONAL_FLOW : BUSINESS_FLOW
  const currentIndex = flow.indexOf(currentPath)
  const targetIndex = flow.indexOf(targetPath)

  // If either path is not in the flow, return true to be safe
  if (currentIndex === -1 || targetIndex === -1) {
    return true
  }

  return targetIndex > currentIndex
}

export const useNavigationValidation = () => {
  const { state } = useFormContext()
  const { triggerValidation } = useFormValidation()

  const validateNavigation = useCallback((path: string) => {
    const rules = validationRules[path]
    if (!rules) return true // If no rules defined, allow navigation

    const { statePath, requiredFields } = rules

    // Get the state object based on the path
    const stateObject = getStateObject(state, statePath as StatePath)

    if (!stateObject) {
      console.error(`State path ${statePath} not found`)
      return false
    }

    // Check if all required fields are filled
    const isValid = requiredFields.every(field => {
      const value = stateObject[field]
      return value !== undefined && value !== null && value !== ''
    })

    return isValid
  }, [state])

  const validateNavigationWithDirection = useCallback((currentPath: string, targetPath: string) => {
    // Only trigger validation if moving forward
    if (isMovingForward(currentPath, targetPath)) {
      triggerValidation()
    }
    return validateNavigation(targetPath)
  }, [validateNavigation, triggerValidation])

  return { 
    validateNavigationWithDirection: validateNavigationWithDirection,
    validateNavigation: validateNavigation
  }
} 